/**
 * CompleteVisitModal — Termin als durchgeführt markieren.
 *
 * PATCH /visits/:id { status: "done", shortMinutes? }
 */
import {
  Alert,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useTranslation } from "react-i18next";
import { useUpdateVisit } from "../../hooks/useVisits.js";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

interface CompleteVisitModalProps {
  opened: boolean;
  onClose: () => void;
  visitId: string;
  scheduledAt: string;
  customerName?: string;
}

interface CompleteFormValues {
  shortMinutes: string;
}

export function CompleteVisitModal({
  opened,
  onClose,
  visitId,
  scheduledAt,
  customerName,
}: CompleteVisitModalProps) {
  const { t } = useTranslation();
  const update = useUpdateVisit(visitId);

  const form = useForm<CompleteFormValues>({
    initialValues: { shortMinutes: "" },
  });

  function handleClose() {
    form.reset();
    onClose();
  }

  async function handleSubmit(values: CompleteFormValues) {
    const trimmed = values.shortMinutes.trim();
    const payload = {
      status: "done" as const,
      ...(trimmed ? { shortMinutes: trimmed } : {}),
    };
    try {
      await update.mutateAsync(payload);
      handleClose();
    } catch {
      // wird über update.error angezeigt
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={t("pages.visits.completeTitle")}
      size="md"
      closeOnClickOutside={false}
    >
      <form onSubmit={form.onSubmit((values) => void handleSubmit(values))}>
        <Stack gap="md">
          <Stack gap={4}>
            {customerName && (
              <Text size="sm">
                <Text span fw={600}>{t("pages.visits.customerLabel")} </Text>
                {customerName}
              </Text>
            )}
            <Text size="sm">
              <Text span fw={600}>{t("pages.visits.dateLabel")} </Text>
              {formatDate(scheduledAt)}
            </Text>
          </Stack>

          <Text size="sm" c="dimmed">
            {t("pages.visits.completeConfirm")}
          </Text>

          <Textarea
            label={t("pages.visits.completeShortMinutes")}
            placeholder={t("pages.visits.completeShortMinutesPlaceholder")}
            value={form.values.shortMinutes}
            onChange={(e) => form.setFieldValue("shortMinutes", e.currentTarget.value)}
            minRows={4}
            autosize
          />

          {update.isError && (
            <Alert color="red" title={t("common.error")}>
              {update.error instanceof Error
                ? update.error.message
                : t("pages.visits.completeError")}
            </Alert>
          )}

          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={handleClose} disabled={update.isPending}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" color="green" loading={update.isPending}>
              {t("pages.visits.completeSubmit")}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
